import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Leaf, Check, Mail, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { getQuizQuestions, submitQuiz, getPublicSettings, trackAnalyticsEvent } from '@/lib/api';
import { toast } from 'sonner';

const EmbedQuiz = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'embed';
  
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [settings, setSettings] = useState({
    email_capture_required: false,
    show_top_protocols: 1
  });
  const [showWelcome, setShowWelcome] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [questionsData, settingsData] = await Promise.all([
        getQuizQuestions(),
        getPublicSettings()
      ]);
      
      if (questionsData && questionsData.length > 0) {
        setQuestions(questionsData);
      } else {
        setQuestions(getDefaultQuestions());
      }
      
      if (settingsData) {
        setSettings(settingsData);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      setQuestions(getDefaultQuestions());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultQuestions = () => [
    {
      id: 'q1',
      question_text: 'What would you most like to improve right now?',
      question_type: 'single_select',
      answers: [
        { answer_value: 'digestive', answer_label: 'Digestive health' },
        { answer_value: 'energy', answer_label: 'Energy & fatigue' },
        { answer_value: 'stress', answer_label: 'Stress & mood' },
        { answer_value: 'sleep', answer_label: 'Sleep quality' },
        { answer_value: 'immune', answer_label: 'Immune health' },
        { answer_value: 'hormone', answer_label: 'Hormone balance' },
        { answer_value: 'blood_sugar', answer_label: 'Blood sugar balance' },
        { answer_value: 'joint', answer_label: 'Joint comfort' },
        { answer_value: 'heart', answer_label: 'Heart health' },
        { answer_value: 'general', answer_label: 'General wellness' },
      ]
    },
    // ... same default questions as QuizPage
  ];

  const totalSteps = questions.length + 1; // +1 for email capture
  const progress = showWelcome ? 0 : ((currentStep + 1) / totalSteps) * 100;
  const isEmailStep = currentStep === questions.length;
  const currentQuestion = questions[currentStep];

  const handleAnswerSelect = (answerValue) => {
    if (!currentQuestion) return;
    
    const questionId = currentQuestion.id;
    const isMultiSelect = currentQuestion.question_type === 'multi_select';
    
    if (isMultiSelect) {
      const current = answers[questionId] || [];
      if (current.includes(answerValue)) {
        setAnswers({
          ...answers,
          [questionId]: current.filter(v => v !== answerValue)
        });
      } else {
        setAnswers({
          ...answers,
          [questionId]: [...current, answerValue]
        });
      }
    } else {
      setAnswers({
        ...answers,
        [questionId]: [answerValue]
      });
    }
  };

  const isAnswerSelected = (answerValue) => {
    if (!currentQuestion) return false;
    const questionAnswers = answers[currentQuestion.id] || [];
    return questionAnswers.includes(answerValue);
  };

  const canProceed = () => {
    if (isEmailStep) {
      if (settings.email_capture_required) {
        return email && email.includes('@');
      }
      return true;
    }
    if (!currentQuestion) return false;
    const questionAnswers = answers[currentQuestion.id] || [];
    return questionAnswers.length > 0;
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      setShowWelcome(true);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const result = await submitQuiz(answers, email || null, source);
      sessionStorage.setItem('quizResult', JSON.stringify(result));
      navigate('/results?embed=true');
    } catch (error) {
      console.error('Failed to submit quiz:', error);
      toast.error('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startQuiz = () => {
    setShowWelcome(false);
    setCurrentStep(0);
    trackAnalyticsEvent('quiz_start', { source });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <div className="text-center">
          <Leaf className="w-10 h-10 text-[#4A6763] mx-auto animate-pulse" />
          <p className="mt-4 text-stone-600">Loading quiz...</p>
        </div>
      </div>
    );
  }

  // Welcome Screen
  if (showWelcome) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-[#4A6763]/10 flex items-center justify-center mx-auto mb-6">
            <Leaf className="w-10 h-10 text-[#4A6763]" strokeWidth={1.5} />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl text-[#2C2C2C] mb-4">
            Find Your Perfect Supplements
          </h1>
          <p className="text-stone-600 mb-8">
            Answer a few simple questions to receive personalized supplement recommendations 
            based on your unique wellness needs.
          </p>
          <Button
            onClick={startQuiz}
            className="btn-primary px-10 py-4 text-base"
            data-testid="start-embed-quiz-btn"
          >
            Start the Quiz
            <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
          </Button>
          <p className="mt-6 text-xs text-stone-400">
            Takes about 2 minutes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-stone-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-stone-600 hover:text-[#4A6763] transition-colors"
              data-testid="embed-back-btn"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm">Back</span>
            </button>
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-[#4A6763]" strokeWidth={1.5} />
              <span className="font-serif text-lg text-[#2C2C2C]">Supplement Finder</span>
            </div>
            <span className="text-sm text-stone-500">
              {currentStep + 1} of {totalSteps}
            </span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </header>

      {/* Quiz Content */}
      <main className="flex-1 flex items-center py-12">
        <div className="max-w-2xl mx-auto px-6 w-full">
          {isEmailStep ? (
            // Email Capture Step
            <div className="animate-fade-in text-center">
              <div className="w-16 h-16 rounded-full bg-[#4A6763]/10 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-[#4A6763]" strokeWidth={1.5} />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl text-[#2C2C2C] mb-4">
                Almost there!
              </h2>
              <p className="text-stone-600 mb-8 max-w-md mx-auto">
                {settings.email_capture_required 
                  ? 'Enter your email to receive your personalized supplement plan.'
                  : 'Enter your email to receive your personalized supplement plan and exclusive wellness tips. (Optional)'
                }
              </p>
              <div className="max-w-sm mx-auto mb-8">
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-14 text-center rounded-full border-stone-200 focus:border-[#4A6763] focus:ring-[#4A6763]"
                  data-testid="embed-email-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || (settings.email_capture_required && !email)}
                  className="btn-primary px-10 py-4"
                  data-testid="embed-submit-btn"
                >
                  {submitting ? 'Processing...' : 'Get My Results'}
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
                {!settings.email_capture_required && (
                  <Button
                    variant="ghost"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="text-stone-500"
                    data-testid="embed-skip-btn"
                  >
                    Skip & View Results
                  </Button>
                )}
              </div>
            </div>
          ) : currentQuestion ? (
            // Question Step
            <div className="animate-fade-in">
              <p className="text-xs uppercase tracking-widest text-[#4A6763] font-medium mb-4 text-center">
                Question {currentStep + 1}
              </p>
              <h2 className="font-serif text-2xl md:text-3xl text-[#2C2C2C] mb-8 text-center">
                {currentQuestion.question_text}
              </h2>
              
              {currentQuestion.question_type === 'multi_select' && (
                <p className="text-sm text-stone-500 mb-6 text-center">
                  Select all that apply
                </p>
              )}

              <div className="grid gap-3 mb-8">
                {currentQuestion.answers?.map((answer, index) => (
                  <button
                    key={answer.answer_value}
                    onClick={() => handleAnswerSelect(answer.answer_value)}
                    className={`quiz-option flex items-center gap-4 text-left ${
                      isAnswerSelected(answer.answer_value) ? 'selected' : ''
                    }`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`embed-answer-${answer.answer_value}`}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isAnswerSelected(answer.answer_value)
                        ? 'border-[#4A6763] bg-[#4A6763]'
                        : 'border-stone-300'
                    }`}>
                      {isAnswerSelected(answer.answer_value) && (
                        <Check className="w-4 h-4 text-white" strokeWidth={2} />
                      )}
                    </div>
                    <span className="text-[#2C2C2C]">{answer.answer_label}</span>
                  </button>
                ))}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="btn-primary px-10"
                  data-testid="embed-next-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 w-5 h-5" strokeWidth={1.5} />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {/* Footer Disclaimer */}
      <footer className="py-4 border-t border-stone-100 bg-white">
        <p className="text-xs text-stone-400 text-center max-w-2xl mx-auto px-6">
          This quiz is for educational purposes only and is not medical advice. 
          Please consult your healthcare practitioner before beginning any nutritional program.
        </p>
      </footer>
    </div>
  );
};

export default EmbedQuiz;
